/**
 * scaffoldDefaults.js
 *
 * Pré-preenchimento inteligente do formulário após seleção de entidade no Escopo.
 * Disparado apenas em criação (isEdit = false).
 */

/**
 * Gera defaults para professional_site ou link_in_bio a partir do profissional selecionado.
 *
 * @param {Object} professional - Entidade do profissional com name, specialty, bio, photoUrl, phone, email
 * @param {string} siteType - 'professional_site' | 'link_in_bio'
 * @param {Array}  placesSchedules - Array de { schedules: [{ id, active }] }
 * @returns {Object} Campos para form.setValue: { identity, seo, blocks }
 */
export function scaffoldProfessional(professional, siteType, placesSchedules = []) {
  const allSchedules = placesSchedules
    .flatMap((ps) => ps.schedules || [])
    .filter((s) => s.active);
  const scheduleIds = allSchedules.map((s) => s.id);

  const blocks = [];
  let order = 0;

  blocks.push({
    id: crypto.randomUUID(),
    type: 'hero',
    enabled: true,
    order: order++,
    config: {
      title: professional.name || '',
      subtitle: professional.specialty || professional.title || '',
    },
  });

  if (professional.bio || professional.description) {
    blocks.push({
      id: crypto.randomUUID(),
      type: 'about',
      enabled: true,
      order: order++,
      config: { html: professional.bio || professional.description },
    });
  }

  if (scheduleIds.length > 0) {
    blocks.push({
      id: crypto.randomUUID(),
      type: 'schedules',
      enabled: true,
      order: order++,
      config: { scheduleIds, ctaLabel: 'Agendar' },
    });
  }

  if (professional.phone || professional.email) {
    blocks.push({
      id: crypto.randomUUID(),
      type: 'contact',
      enabled: true,
      order: order++,
      config: { phone: professional.phone || '', email: professional.email || '' },
    });
  }

  return {
    identity: {
      avatarUrl: professional.photoUrl || null,
      avatarShape: 'round',
    },
    seo: {
      title: professional.name || '',
      description: professional.description || professional.bio || '',
      ogImageUrl: '',
    },
    blocks,
  };
}

/**
 * Gera defaults para company_site a partir do local selecionado.
 *
 * @param {Object} place - Entidade do local com name, description, addressStreet, city, state, phone, email
 * @returns {Object} Campos para form.setValue: { seo, blocks }
 */
export function scaffoldCompany(place) {
  const blocks = [];
  let order = 0;

  blocks.push({
    id: crypto.randomUUID(),
    type: 'hero',
    enabled: true,
    order: order++,
    config: {
      title: place.name || '',
      subtitle: place.description || '',
    },
  });

  if (place.addressStreet || place.city) {
    blocks.push({
      id: crypto.randomUUID(),
      type: 'locations',
      enabled: true,
      order: order++,
      config: {
        items: [
          {
            id: crypto.randomUUID(),
            name: place.name,
            address: [place.addressStreet, place.city, place.state].filter(Boolean).join(', '),
          },
        ],
      },
    });
  }

  if (place.phone || place.email) {
    blocks.push({
      id: crypto.randomUUID(),
      type: 'contact',
      enabled: true,
      order: order++,
      config: { phone: place.phone || '', email: place.email || '' },
    });
  }

  return {
    seo: {
      title: place.name || '',
      description: place.description || '',
      ogImageUrl: '',
    },
    blocks,
  };
}
